/* The charts this visitor has already saved, listed where they build one.
 *
 * Same idea as SavedCompatReports on the matching page, and the same reason:
 * someone who has entered their birth details once should not type them again,
 * and a returning visitor arriving on a blank form has no way of knowing the
 * app already knows them.
 *
 * Real data, not a placeholder list: profiles come from AuthContext
 * (user_profiles) and their birth details from user_birth_details, which is the
 * same pair KundliSection reads. Picking one hands the birth data straight to
 * the calculator rather than refilling the form and making the visitor press
 * the button again.
 */

import { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { Birth, tzOffsetHours } from "../../utils/calculatorApi";

type Row = {
  id: string;
  name: string;
  date_of_birth: string | null;
  time_of_birth: string | null;
  birth_place: string | null;
  birth_lat: string | null;
  birth_lng: string | null;
  birth_timezone: string | null;
};

/* Stored times are not consistently 24-hour: some rows carry "07:32 AM" from
   an older form. Normalise rather than trusting the column. */
const to24 = (t: string): string => {
  const m = t.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!m) return t.slice(0, 5);
  let h = Number(m[1]);
  const mer = m[3]?.toUpperCase();
  if (mer === "PM" && h !== 12) h += 12;
  if (mer === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m[2]}`;
};

const readableDate = (iso: string) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function SavedKundlis({
  onPick,
}: {
  /** Hand the birth data straight to the calculator. */
  onPick: (birth: Birth, name: string, place: string) => void;
}) {
  const { user, userProfiles } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!user || !userProfiles?.length) {
      setRows([]);
      setLoading(false);
      return;
    }
    (async () => {
      const ids = userProfiles.map((p) => p.id);
      const { data } = await supabase
        .from("user_birth_details")
        .select(
          "profile_id, date_of_birth, time_of_birth, birth_place, birth_lat, birth_lng, birth_timezone",
        )
        .in("profile_id", ids);
      if (!alive) return;
      const byId = new Map((data || []).map((d: any) => [d.profile_id, d]));
      setRows(
        userProfiles
          .map((p) => {
            const d = byId.get(p.id);
            return d
              ? {
                  id: p.id,
                  name: p.name,
                  date_of_birth: d.date_of_birth,
                  time_of_birth: d.time_of_birth,
                  birth_place: d.birth_place,
                  birth_lat: d.birth_lat,
                  birth_lng: d.birth_lng,
                  birth_timezone: d.birth_timezone,
                }
              : null;
          })
          /* Only rows we can actually compute a chart from: a profile with no
             coordinates would produce a chart for the wrong place. */
          .filter(
            (r): r is Row =>
              !!r && !!r.date_of_birth && !!r.birth_lat && !!r.birth_lng,
          ),
      );
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [user, userProfiles]);

  if (loading) return <p className="kchart-saved__empty">Loading your kundlis…</p>;

  if (!rows.length)
    return (
      <p className="kchart-saved__empty">
        No saved kundlis yet. Build one and it will appear here.
      </p>
    );

  return (
    <ul className="kchart-saved__list">
      {rows.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            className="kchart-saved__item"
            onClick={() =>
              onPick(
                {
                  date: r.date_of_birth as string,
                  time: to24(r.time_of_birth || "12:00"),
                  lat: Number(r.birth_lat),
                  lon: Number(r.birth_lng),
                  tz: tzOffsetHours(
                    r.birth_timezone || "Asia/Kolkata",
                    r.date_of_birth as string,
                  ),
                },
                r.name,
                r.birth_place || "",
              )
            }
          >
            <span className="kchart-saved__name">{r.name}</span>
            <span className="kchart-saved__meta">
              {r.date_of_birth ? readableDate(r.date_of_birth) : ""}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
